/**
 * O ÚNICO arquivo do cliente que conhece WebGL e canvas.
 *
 * Fica fino de propósito: nada aqui é coberto por teste em node — WebGL não existe
 * fora do browser. Toda lógica testável mora em `scene.ts`, `world.ts` e `loop.ts`,
 * e o que sobra aqui é aplicação de tamanho e a chamada de desenho, verificadas
 * no browser e depois no E2E.
 */
import type { PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import { WebGLRenderer as Renderer } from 'three';

export interface RendererHandle {
  readonly raw: WebGLRenderer;
  /**
   * `width`/`height` em CSS px; `scale` é a escala de resolução do controlador.
   * O CSS continua do tamanho do layout — só o buffer de desenho encolhe.
   */
  setViewport(width: number, height: number, scale: number): void;
  draw(scene: Scene, camera: PerspectiveCamera): void;
  dispose(): void;
}

/**
 * Teto de densidade de pixel. Celular topo de linha reporta 3 ou mais, e desenhar
 * a 3x é queimar bateria por diferença que ninguém vê a um palmo do rosto.
 * Quem adapta de verdade é o `RenderScaleController` (PERF.yaml, render_scale).
 */
const MAX_PIXEL_RATIO = 2;

export function createRenderer(canvas: HTMLCanvasElement): RendererHandle {
  const renderer = new Renderer({
    canvas,
    // Antialias custa caro em GPU móvel e a resolução dinâmica já suaviza.
    antialias: false,
    powerPreference: 'high-performance',
  });

  let appliedWidth = 0;
  let appliedHeight = 0;
  let appliedRatio = 0;

  return {
    raw: renderer,

    setViewport(width, height, scale) {
      if (width <= 0 || height <= 0) return;
      const deviceRatio = Math.min(globalThis.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const ratio = deviceRatio * scale;
      // `setSize` reconfigura o buffer: chamar todo frame é caro à toa.
      if (width === appliedWidth && height === appliedHeight && ratio === appliedRatio) return;
      appliedWidth = width;
      appliedHeight = height;
      appliedRatio = ratio;
      renderer.setPixelRatio(ratio);
      // `false`: o tamanho em CSS é do layout, NEVER do renderer.
      renderer.setSize(width, height, false);
    },

    draw(scene, camera) {
      renderer.render(scene, camera);
    },

    dispose() {
      renderer.dispose();
    },
  };
}
